import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { RootState } from '../../../store';
import {
  fetchStages,
  deleteStage,
  setFilters,
  clearFilters,
  selectStages,
  selectStagesLoading,
  selectStagesError,
  selectStagesFilters
} from '../store/stageSlice';
import { useAppDispatch } from '../../../store';
import { Stage, STAGE_CATEGORIES, STAGE_STATUSES } from '../types/stage';
import StageForm from '../components/stages/StageForm';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { usePermission } from '../../../components/common/PermissionGuard';
import PageWrapper from '../../../components/common/PageWrapper';

const Stages: React.FC = () => {
  const dispatch = useAppDispatch();
  const stages = useSelector(selectStages);
  const loading = useSelector(selectStagesLoading);
  const error = useSelector(selectStagesError);
  const filters = useSelector(selectStagesFilters);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState<Stage | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedStages, setExpandedStages] = useState<Set<string>>(new Set());

  // Permissions
  const canCreate = usePermission('stages', 'create');
  const canUpdate = usePermission('stages', 'update');
  const canDelete = usePermission('stages', 'delete');
  const canRead = usePermission('stages', 'read');

  useEffect(() => {
    if (canRead) {
      dispatch(fetchStages(filters));
    }
  }, [dispatch, filters, canRead]);

  const handleCreateStage = () => {
    setSelectedStage(null);
    setFormMode('create');
    setIsFormOpen(true);
  };

  const handleEditStage = (stage: Stage) => {
    setSelectedStage(stage);
    setFormMode('edit');
    setIsFormOpen(true);
  };

  const handleDeleteStage = async (stage: Stage) => {
    if (window.confirm(`Are you sure you want to delete "${stage.name}"?`)) {
      try {
        await dispatch(deleteStage(stage.id)).unwrap();
      } catch (error) {
        console.error('Error deleting stage:', error);
      }
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    dispatch(setFilters({ search: value }));
  };

  const handleFilterChange = (key: keyof typeof filters, value: string | null) => {
    dispatch(setFilters({ [key]: value }));
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    dispatch(clearFilters());
  };

  const toggleStageExpansion = (stageId: string) => {
    const newExpanded = new Set(expandedStages);
    if (newExpanded.has(stageId)) {
      newExpanded.delete(stageId);
    } else {
      newExpanded.add(stageId);
    }
    setExpandedStages(newExpanded);
  };

  // Organize stages into tree structure
  const organizeStagesTree = (stages: Stage[]): (Stage & { children: Stage[] })[] => {
    const stageMap = new Map<string, Stage & { children: Stage[] }>();
    const rootStages: (Stage & { children: Stage[] })[] = [];

    // Create a map of all stages with children array
    stages.forEach(stage => {
      stageMap.set(stage.id, { ...stage, children: [] });
    });

    // Build the tree
    stages.forEach(stage => {
      const stageWithChildren = stageMap.get(stage.id)!;
      
      if (stage.parent_stage_id) {
        const parent = stageMap.get(stage.parent_stage_id);
        if (parent) {
          parent.children.push(stageWithChildren);
        }
      } else {
        rootStages.push(stageWithChildren);
      }
    });

    return rootStages;
  };

  const renderStageTree = (stages: (Stage & { children: Stage[] })[], level = 0) => {
    return stages.map(stage => (
      <React.Fragment key={stage.id}>
        <motion.tr
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <td className="px-6 py-4 whitespace-nowrap">
            <div className="flex items-center" style={{ paddingLeft: `${level * 20}px` }}>
              {stage.children.length > 0 && (
                <button
                  onClick={() => toggleStageExpansion(stage.id)}
                  className="mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {expandedStages.has(stage.id) ? (
                    <ChevronDownIcon className="h-4 w-4" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4" />
                  )}
                </button>
              )}
              {stage.children.length === 0 && <div className="w-6" />}
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {stage.name}
                </div>
                {stage.description && (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {stage.description}
                  </div>
                )}
              </div>
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
              {stage.category || 'N/A'}
            </span>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
            {stage.estimated_duration_minutes ? `${stage.estimated_duration_minutes} min` : 'N/A'}
          </td>
          <td className="px-6 py-4 whitespace-nowrap">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              stage.is_parallel 
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
            }`}>
              {stage.is_parallel ? 'Yes' : 'No'}
            </span>
          </td>
          <td className="px-6 py-4 whitespace-nowrap">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              stage.status === 'active'
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
            }`}>
              {stage.status}
            </span>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
            <div className="flex items-center justify-end space-x-2">
              {canUpdate && (
                <button
                  onClick={() => handleEditStage(stage)}
                  className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => handleDeleteStage(stage)}
                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </td>
        </motion.tr>
        {expandedStages.has(stage.id) && stage.children.length > 0 && (
          <tr>
            <td colSpan={6} className="px-0">
              {renderStageTree(stage.children as (Stage & { children: Stage[] })[], level + 1)}
            </td>
          </tr>
        )}
      </React.Fragment>
    ));
  };

  if (!canRead) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Access Denied</h3>
          <p className="text-gray-500 dark:text-gray-400">You don't have permission to view stages.</p>
        </div>
      </div>
    );
  }

  const organizedStages = organizeStagesTree(stages);

  return (
    <PageWrapper >
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Stages Management</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage workflow stages and their hierarchy
          </p>
        </div>
        {canCreate && (
          <button
            onClick={handleCreateStage}
            className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Create Stage
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search stages..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Filters
            </button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700"
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Category
                  </label>
                  <select
                    value={filters.category || ''}
                    onChange={(e) => handleFilterChange('category', e.target.value || null)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">All Categories</option>
                    {STAGE_CATEGORIES.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status
                  </label>
                  <select
                    value={filters.status || ''}
                    onChange={(e) => handleFilterChange('status', e.target.value || null)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">All Statuses</option>
                    {STAGE_STATUSES.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    onClick={clearAllFilters}
                    className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
        </div>
      )}

      {/* Stages Table */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Stage Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Parallel
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {organizedStages.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      No stages found. {canCreate && 'Create your first stage to get started.'}
                    </td>
                  </tr>
                ) : (
                  renderStageTree(organizedStages)
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Stage Form Modal */}
      <StageForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        stage={selectedStage}
        mode={formMode}
      />
    </div>
    </PageWrapper>
  );
};

export default Stages;
